import { inject, Injectable, signal } from '@angular/core';
import { ComponentsCategoriesFields, ComponentsCategoriesFormArray, ComponentsFe, ComponentsFields, ComponentsFormGroups, ComponentsValidators, ComponentsWithCustomCategories } from '../components.models';
import { CustomFormArray, CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { Validators } from '@angular/forms';
import { AlternativeTranslate, Components, ComponentsCategories } from 'tilby-models';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ComponentDetailsFormService {
 
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly translate = inject(TranslateService);
  
  private componentForm!: ComponentsFormGroups;
  componentFormReady = signal(false);

  get form() {
    return this.componentForm;
  }

  validators: ComponentsValidators = {
    nameMaxLength: 255,

    // --------
    provMinLength: 0,
    provMaxLength: 255,
    provMaxLengthArray: new Array(10).fill(2)
  };

  createForm(component: ComponentsFields = new ComponentsFe() ,hiddenFields?:Array<keyof ComponentsFields>) {
    this.componentForm = new CustomFormGroup<ComponentsWithCustomCategories>({
      id: new CustomFormControl(
        component.id || null
      ),
      alternative_names: new CustomFormControl(
        (component.alternative_names || []).map(alt => ({...alt}))
      ),
      name: new CustomFormControl(
        component.name || null,
        { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength)] }
      ),
      price_difference: new CustomFormControl(
        component.price_difference != null ? component.price_difference : null
      ),
      categories: new CustomFormArray<CustomFormGroup<ComponentsCategoriesFormArray>>([])
    });

    this.initCategories(component.categories || []);

    this.componentFormReady.set(true);
    return this.componentForm;
  }
  
  resetFormValues(component: Components = new ComponentsFe()) {
    this.componentForm.patchValue({
      id: component.id || null,
      name: component.name || null,
      alternative_names: (component.alternative_names || []).map((alt: AlternativeTranslate) => ({...alt})),
      price_difference: component.price_difference != null ? component.price_difference : null
    });

    this.categoriesArray?.controls.forEach(control => {
      control.get('checked')?.setValue(false);
    })

    this.initCategoriesValues(component.categories || []);

    this.componentForm.markAsPristine();
  }

  initCategoriesValues(categories: ComponentsCategories[]) {
    const selectedIds = new Set(categories.map((c: ComponentsCategories) => c.id));
    
    let allSelected = true;
    let hasRealCategories = false;

    this.categoriesArray?.controls.forEach((control) => {
      const id = control.get('id')?.value;
      if (id === 0) return;

      hasRealCategories = true;

      // Verifico se questa categoria è presente nel set delle selezionate
      const isChecked = selectedIds.has(id);
      control.get('checked')?.setValue(isChecked, { emitEvent: false });

      // Se ne trovo anche solo una non selezionata, il flag globale diventa false
      if (!isChecked) {
        allSelected = false;
      }
    });

    // Aggiorno lo stato della checkbox "Seleziona tutto" (Indice 0)
    const selectAllControl = this.categoriesArray?.at(0);
    if (selectAllControl && selectAllControl.get('id')?.value === 0) {
      const selectAllState = hasRealCategories && allSelected;
      selectAllControl.get('checked')?.setValue(selectAllState, { emitEvent: false });
    }
  }

  initCategories(categories: ComponentsCategories[]) {
    const selectedCategories = new Set(categories.map((c: ComponentsCategories) => c.id));

    [...this.itemDataCatalogService.getDataByKey("categories") ?? []].forEach((category: ComponentsCategories) => {
      this.categoriesArray?.push(new CustomFormGroup<ComponentsCategoriesFormArray>({
        id: new CustomFormControl(
          category.id || null
        ),
        name: new CustomFormControl(
          category.name || null
        ),
        checked: new CustomFormControl(
          selectedCategories.has(category.id)
        )
      }))
    })

    if(this.categoriesArray?.length) {
      const allCategoriesSelected = this.categoriesArray?.value.every((c: ComponentsCategoriesFields) => c.checked);
      this.categoriesArray.insert(0, new CustomFormGroup<ComponentsCategoriesFormArray>({
        id: new CustomFormControl(
          0
        ),
        name: new CustomFormControl(
          this.translate.instant('ITEMS.COMPONENTS.SELECT_ALL')
        ),
        checked: new CustomFormControl(
          allCategoriesSelected
        )
      }))
    }
  }

  getComponentFields(currentComponent: Components = new ComponentsFe()): Partial<Components> {
    // Clono l'oggetto di destinazione per non mutare l'originale
    const updatedComponent = { ...currentComponent }; 

    // Recupero tutte le chiavi (nomi dei campi) presenti nel form
    const componentFieldsToMap = Object.keys(this.componentForm.controls);

    // Ciclo su tutte le chiavi trovate nel form
    componentFieldsToMap.forEach((key: string) => {

      const control = this.componentForm.get(key);

      if(control) {
        let value = control.value;

        if (key === 'categories' && Array.isArray(value)) {
          value = value
            .filter((component: ComponentsCategoriesFields) => component.checked && component.id !== 0)
            .map((component: ComponentsCategoriesFields) => ({
                  id: component.id,
                  name: component.name
            }));
        }

        // Assegno il valore all'oggetto
        (updatedComponent as any)[key] = value
      }
    })
    return updatedComponent;
  }

  get id() {
    return this.componentForm.controls.id;
  }

  get name() {
    return this.componentForm.controls.name;
  }

  get alternativeNames() {
    return this.componentForm.controls.alternative_names;
  }

  get price_difference() {
    return this.componentForm.controls.price_difference;
  }

  get categoriesArray() {
    return this.componentForm.controls.categories;
  }
}